import { getPlayerConfirmed } from '@sportninja/common/features/game/attendance/utils/attendanceConfirm'
import { isWaiverLocked } from '@sportninja/common/features/game/attendance/utils/waiverLock'
import { useEffect, useState } from 'react'
import type { AttendancePlayer } from '../types'
import { useAttendanceSection } from '../components/cells/AttendanceSectionContext'

/**
 * CONFIRM / PROMOTE state for one attendance row: the confirmed checkbox, the
 * optimistic promote/demote toggle and the de-promote / withdraw gates.
 */
export const useSpareConfirmState = (player: AttendancePlayer) => {
  const {
    waiverEnforced,
    forceConfirmed,
    isSpareCandidate,
    promotedRequestById,
    onPromoteSpare,
    onDemoteSpare,
  } = useAttendanceSection()
  const promotedRequestId = promotedRequestById?.[player.id]

  const availabilityRequest = player.availability_request
  const isSpareRow = !!availabilityRequest
  // Server-truth promoted state (is_promoted). The CONF checkbox is a toggle:
  // promoted → demote, not-promoted (and 'available') → promote.
  const serverPromoted = isSpareRow && !!player.is_promoted
  const isAvailable = isSpareRow && availabilityRequest?.status === 'available'

  // Optimistic override during an in-flight promote/demote (null = follow
  // server truth). Reverts on failure.
  const [pendingPromoted, setPendingPromoted] = useState<boolean | null>(null)
  // In-flight flag for the Roster Spares de-promote (which has no optimistic
  // checkbox state — the row just drops out on refetch).
  const [dePromoting, setDePromoting] = useState(false)
  const inFlight = pendingPromoted !== null || dePromoting

  // Hold the optimistic value until the refetched player actually reflects it,
  // then release — avoids a one-frame back-and-forth to stale server truth.
  useEffect(() => {
    if (pendingPromoted !== null && serverPromoted === pendingPromoted) {
      setPendingPromoted(null)
    }
  }, [pendingPromoted, serverPromoted])

  const waiverLocked = isWaiverLocked(player, waiverEnforced, isSpareCandidate)

  // Roster Spares row that is a promoted spare candidate: CONFIRM is pressable
  // and de-promotes.
  const canDePromote = !isSpareCandidate && !!promotedRequestId

  const confirmed = isSpareRow
    ? pendingPromoted ?? serverPromoted
    : // Roster / roster-spares: an unsigned-waiver player can't be confirmed, so
      // force the checkmark off regardless of forceConfirmed — except a
      // promoted candidate, which stays checked so it can still be de-promoted
      // (removing a player needs no waiver).
      (!waiverLocked || canDePromote) &&
      (forceConfirmed || getPlayerConfirmed(player))
  // Spare rows toggle promote/demote (disabled while a call is in flight, and
  // promote requires an 'available' request). Regular rows are display-only,
  // EXCEPT promoted-spare-candidate roster spares, which can de-promote.
  const confirmDisabled = isSpareRow
    ? inFlight || (!serverPromoted && !isAvailable)
    : canDePromote
    ? inFlight
    : true
  // Checked CONFIRM box that is actually pressable to de-promote — the only
  // confirm state with an action worth a tooltip.
  const canDePromoteNow = canDePromote && !confirmDisabled
  const canPromote = isSpareRow && !confirmed && isAvailable
  // Any non-promoted spare row can be withdrawn, regardless of request status.
  const canWithdraw = isSpareRow && !confirmed

  const onToggleConfirm = () => {
    // Only spare rows have an interactive CONF cell.
    if (!isSpareRow || inFlight) {
      return
    }
    const next = !confirmed
    const action = next ? onPromoteSpare : onDemoteSpare
    // Promote requires an available request; nothing to do without a handler.
    if (!action || (next && !isAvailable) || !availabilityRequest) {
      return
    }
    setPendingPromoted(next)
    // Failure: revert to server truth. Success: keep the optimistic value — the
    // effect drops it once the refetched is_promoted matches.
    action(availabilityRequest.id).then((ok) => {
      if (!ok) {
        setPendingPromoted(null)
      }
    })
  }

  const onDePromote = () => {
    if (!canDePromote || inFlight || !onDemoteSpare || !promotedRequestId) {
      return
    }
    setDePromoting(true)
    onDemoteSpare(promotedRequestId).finally(() => setDePromoting(false))
  }

  return {
    confirmed,
    confirmDisabled,
    canDePromote,
    canDePromoteNow,
    canPromote,
    canWithdraw,
    inFlight,
    onToggleConfirm,
    onDePromote,
  }
}
